import api from './api';
import practiceService from './practiceService';
import { resolveDisplayScheduleTargets, hasAnyScheduleTargets } from '../utils/scheduleTargetUtils';
import { getUsePatientScheduleTargetsPreference } from '../utils/patientScheduleTargetPreference.js';

// const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

// Create axios instance with default config
// const api = axios.create({
//   baseURL: API_URL,
//   withCredentials: true,
//   headers: {
//     'Content-Type': 'application/json',
//   },
// });

const patientService = {
  // Create patient
  createPatient: async (practiceId, patientData) => {
    const isFormData = patientData instanceof FormData;
    const config = isFormData ? {
      headers: { 'Content-Type': 'multipart/form-data' }
    } : {};
    const response = await api.post(`/practices/${practiceId}/patients`, patientData, config);
    return response;
  },

  // Get all patients for a practice
  getPatients: async (practiceId, params = {}) => {
    const response = await api.get(`/practices/${practiceId}/patients`, { params });
    return response;
  },

  // Get patient by ID
  getPatient: async (practiceId, patientId) => {
    const response = await api.get(`/practices/${practiceId}/patients/${patientId}`);
    return response;
  },

  // Fast patient details for initial UI render
  getPatientDetailsFast: async (practiceId, patientId, options = {}) => {
    const params = {};
    if (options.auditView) params.auditView = 1;
    const response = await api.get(`/practices/${practiceId}/patients/${patientId}/details-fast`, { params });
    return response;
  },

  /**
   * Record a client-side patient activity (PDF/CSV export or email report) on the patient event log.
   * Failures are swallowed so export/email UX is never blocked.
   */
  logPatientExport: (practiceId, patientId, { format, source } = {}) => {
    if (!practiceId || !patientId) return Promise.resolve();
    const normalized = String(format || '').toLowerCase();
    const action = normalized === 'csv'
      ? 'export_csv'
      : normalized === 'email' || normalized === 'email_report'
        ? 'email_report'
        : 'export_pdf';
    return api.post(`/practices/${practiceId}/patients/${patientId}/activity-logs`, {
      action,
      source: source || null
    }).catch((err) => {
      console.warn('Failed to log patient export:', err?.message || err);
    });
  },

  /**
   * Lightweight patient header for measurement/chart pages (name, DOB, vitals).
   * Same payload as getPatientDetailsFast — kept as a named API for chart callers.
   */
  getMeasurementHeaderFast: async (practiceId, patientId) => {
    const response = await api.get(`/practices/${practiceId}/patients/${patientId}/details-fast`);
    return response;
  },

  // Get next account number for a practice
  getNextAccountNumber: async (practiceId) => {
    const response = await api.get(`/practices/${practiceId}/patients/next-account-number`);
    return response;
  },

  resetPatientPassword: async (practiceId, patientId, { password, confirmPassword, emailPatient = false, signInPdf = '' }) => {
    const response = await api.post(
      `/practices/${practiceId}/patients/${patientId}/reset-password`,
      { password, confirmPassword, emailPatient, signInPdf: emailPatient ? signInPdf : '' }
    );
    return response;
  },

  // Update patient
  updatePatient: async (practiceId, patientId, patientData) => {
    const isFormData = patientData instanceof FormData;
    const config = isFormData ? {
      headers: { 'Content-Type': 'multipart/form-data' }
    } : {};
    const response = await api.put(`/practices/${practiceId}/patients/${patientId}`, patientData, config);
    return response;
  },

  // Delete patient
  deletePatient: async (practiceId, patientId) => {
    const response = await api.delete(`/practices/${practiceId}/patients/${patientId}`);
    return response;
  },

  // Get patient details with all related data
  getPatientDetails: async (practiceId, patientId) => {
    const response = await api.get(`/practices/${practiceId}/patients/${patientId}/details`);
    return response;
  },

  // Get patient schedule and targets
  getScheduleAndTargets: async (practiceId, patientId) => {
    const response = await api.get(`/practices/${practiceId}/patients/${patientId}/schedule-targets`);
    return response;
  },

  // Update patient schedule and targets
  updateScheduleAndTargets: async (practiceId, patientId, data) => {
    const response = await api.put(`/practices/${practiceId}/patients/${patientId}/schedule-targets`, data);
    return response;
  },


  // Follow-up notes
  createFollowUp: async (practiceId, patientId, followUpData) => {
    const response = await api.post(`/practices/${practiceId}/patients/${patientId}/follow-ups`, followUpData);
    practiceService.invalidatePracticePatientCaches(practiceId);
    return response;
  },

  getFollowUps: async (practiceId, patientId, careProgram) => {
    const params = {};
    if (careProgram) {
      params.care_program = String(careProgram).toUpperCase();
    }
    const response = await api.get(`/practices/${practiceId}/patients/${patientId}/follow-ups`, { params });
    return response;
  },

  updateFollowUp: async (practiceId, patientId, followUpId, followUpData) => {
    const response = await api.put(`/practices/${practiceId}/patients/${patientId}/follow-ups/${followUpId}`, followUpData);
    practiceService.invalidatePracticePatientCaches(practiceId);
    return response;
  },

  deleteFollowUp: async (practiceId, patientId, followUpId) => {
    const response = await api.delete(`/practices/${practiceId}/patients/${patientId}/follow-ups/${followUpId}`);
    practiceService.invalidatePracticePatientCaches(practiceId);
    return response;
  },

  getRpmCarePlans: async (practiceId, patientId) => {
    const response = await api.get(`/practices/${practiceId}/patients/${patientId}/rpm-care-plans`);
    return response;
  },

  createRpmCarePlan: async (practiceId, patientId, carePlanData) => {
    const response = await api.post(`/practices/${practiceId}/patients/${patientId}/rpm-care-plans`, carePlanData);
    return response;
  },

  updateRpmCarePlan: async (practiceId, patientId, carePlanId, carePlanData) => {
    const response = await api.put(`/practices/${practiceId}/patients/${patientId}/rpm-care-plans/${carePlanId}`, carePlanData);
    return response;
  },

  // ============================================================================
  // METER MANAGEMENT FUNCTIONS
  // ============================================================================

  // Add meter to patient
  addMeterToPatient: async (practiceId, patientId, meterData) => {
    const response = await api.post(`/practices/${practiceId}/patients/${patientId}/meters`, meterData);
    return response;
  },

  // Update meter on patient
  updateMeterOnPatient: async (practiceId, patientId, meterId, meterData) => {
    const response = await api.put(
      `/practices/${practiceId}/patients/${patientId}/meters/${meterId}`,
      meterData
    );
    return response;
  },

  // Delete meter from patient
  deleteMeterFromPatient: async (practiceId, patientId, meterId) => {
    const response = await api.delete(`/practices/${practiceId}/patients/${patientId}/meters/${meterId}`);
    return response;
  },

  // Get patient meters
  getPatientMeters: async (practiceId, patientId) => {
    const response = await api.get(`/practices/${practiceId}/patients/${patientId}/meters`);
    return response;
  },

  // Get patient meter mappings (for edit mode)
  getPatientMeterMappings: async (practiceId, patientId) => {
    const response = await api.get(`/practices/${practiceId}/patients/${patientId}/meters`);
    return response;
  },

  // ============================================================================
  // PATIENT LOOKUP FUNCTIONS
  // ============================================================================

  // Lookup patients with filters
  lookupPatient: async (practiceId, filters = {}) => {
    const params = {};

    // Only add non-empty filter parameters
    if (filters.lastName && filters.lastName.trim()) {
      params['lastName'] = filters.lastName.trim();
    }
    if (filters.firstName && filters.firstName.trim()) {
      params['firstName'] = filters.firstName.trim();
    }
    // Don't send patient-practice since practiceId is already in the URL path
    if (filters.provider && filters.provider.toString().trim()) {
      params['patient-provider'] = filters.provider.toString().trim();
    }
    if (filters.caregiver && filters.caregiver.toString().trim()) {
      params['patient-caregiver'] = filters.caregiver.toString().trim();
    }
    if (filters.status && filters.status.toString().trim()) {
      params['patient-status'] = filters.status.toString().trim();
    }
    if (filters.vitals && filters.vitals.toString().trim()) {
      params['patient-vitals'] = filters.vitals.toString().trim();
    }
    if (filters.serialNumber && filters.serialNumber.trim()) {
      params['patient_serial_number'] = filters.serialNumber.trim();
    }
    if (filters.page) {
      params['page'] = filters.page;
    }
    if (filters.limit) {
      params['limit'] = filters.limit;
    }

    const response = await api.get(`/practices/${practiceId}/lookup`, { params });
    return response;
  },

  // Lookup patients by RPM date range
  lookupPatientRPM: async (practiceId, filters = {}) => {
    const params = {};

    // Only add non-empty date parameters
    // Don't send practiceId since it's already in the URL path
    const startDate = filters.start || filters.billingStartRpm || '';
    const endDate = filters.end || filters.billingEndRpm || '';

    if (startDate && startDate.trim()) {
      params['billingStartRpm'] = startDate.trim();
    }
    if (endDate && endDate.trim()) {
      params['billingEndRpm'] = endDate.trim();
    }
    if (filters.page) params['page'] = filters.page;
    if (filters.limit) params['limit'] = filters.limit;

    const response = await api.get(`/practices/${practiceId}/lookup-rpm`, { params });
    return response;
  },

  // Lookup patients by CCM date range
  lookupPatientCCM: async (practiceId, filters = {}) => {
    const params = {};
    const startDate = filters.start || filters.billingStartCcm || '';
    const endDate = filters.end || filters.billingEndCcm || '';

    if (startDate && startDate.trim()) {
      params['billingStartCcm'] = startDate.trim();
    }
    if (endDate && endDate.trim()) {
      params['billingEndCcm'] = endDate.trim();
    }
    if (filters.page) params.page = filters.page;
    if (filters.limit) params.limit = filters.limit;
    [
      'firstName',
      'lastName',
      'phone',
      'dobOperator',
      'dobFrom',
      'dobTo',
      'providerId',
      'caregiverId',
      'systemCaregiverId'
    ].forEach((key) => {
      if (filters[key] != null && String(filters[key]).trim()) {
        params[key] = String(filters[key]).trim();
      }
    });

    const response = await api.get(`/practices/${practiceId}/lookup-ccm`, { params });
    return response;
  },

  // Get current logged-in patient (by user_id)
  getCurrentPatient: async () => {
    const response = await api.get('/patients/me');
    return response;
  },

  // ============================================================================
  // DASHBOARD OPTIMIZATION APIS (fast, lazy-load)
  // ============================================================================

  /**
   * getDashboardSummary — replaces 4 sequential API calls on PatientDashboard.
   * Single endpoint: parallel DB queries + 60s server-side cache.
   * Target response time: < 150ms.
   */
  getDashboardSummary: async (practiceId, patientId) => {
    const response = await api.get(
      `/practices/${practiceId}/patients/${patientId}/dashboard-summary`
    );
    return response;
  },

  /**
   * getPatientNotes — lazy-load, called only when Notes section is expanded.
   */
  getPatientNotes: async (practiceId, patientId, careProgram) => {
    const params = {};
    if (careProgram) {
      params.care_program = String(careProgram).toUpperCase();
    }
    const response = await api.get(
      `/practices/${practiceId}/patients/${patientId}/notes`,
      { params }
    );
    return response;
  },

  /**
   * getMeasurementSummary — lazy-load, called when "Summary of Measurement" tab opens.
   * Returns 7-day, 1-month, 3-month averages for BP, glucose, weight.
   */
  getMeasurementSummary: async (practiceId, patientId) => {
    const response = await api.get(
      `/practices/${practiceId}/patients/${patientId}/measurement-summary`
    );
    return response;
  },

  /**
   * getPatientUploads — lazy-load paginated upload history.
   * @param {number} page  - Page number (default 1)
   * @param {number} limit - Items per page (default 20)
   */
  getPatientUploads: async (practiceId, patientId, page = 1, limit = 20) => {
    const response = await api.get(
      `/practices/${practiceId}/patients/${patientId}/uploads`,
      { params: { page, limit } }
    );
    return response;
  },

  /**
   * getPatientDocuments — fetches files from patient_document table.
   */
  getPatientDocuments: async (practiceId, patientId) => {
    const response = await api.get(
      `/practices/${practiceId}/patients/${patientId}/documents`
    );
    return response;
  },

  reviewPatientMeasurement: async (practiceId, patientId, vitalType, measurementId) => {
    const response = await api.put(
      `/practices/${practiceId}/patients/${patientId}/measurements/${vitalType}/${measurementId}/review`
    );
    return response;
  },

  patchPatientDocument: async (practiceId, patientId, documentId, payload) => {
    const response = await api.patch(
      `/practices/${practiceId}/patients/${patientId}/documents/${documentId}`,
      payload
    );
    return response;
  },

  deletePatientDocument: async (practiceId, patientId, documentId) => {
    const response = await api.delete(
      `/practices/${practiceId}/patients/${patientId}/documents/${documentId}`
    );
    return response;
  },

  fetchPatientDocumentBlob: async (practiceId, patientId, documentId) => {
    const response = await api.get(
      `/practices/${practiceId}/patients/${patientId}/documents/${documentId}/file`,
      { responseType: 'blob' }
    );
    return response;
  },

  uploadPatientDocument: async (practiceId, patientId, file, displayName) => {
    const formData = new FormData();
    formData.append('documents', file);
    if (displayName) formData.append('document_name', displayName);

    const response = await api.post(
      `/practices/${practiceId}/patients/${patientId}/documents`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 0,
        maxBodyLength: Infinity,
        maxContentLength: Infinity,
        suppressGlobal413Toast: true,
      }
    );
    return response;
  },

  getPatientPrograms: async (practiceId, patientId) => {
    const response = await api.get(
      `/practices/${practiceId}/patients/${patientId}/programs`
    );
    return response;
  },

  updatePatientPrograms: async (practiceId, patientId, data) => {
    const response = await api.put(
      `/practices/${practiceId}/patients/${patientId}/programs`,
      data
    );
    return response;
  },

  updatePatientICD: async (practiceId, patientId, data) => {
    const response = await api.put(
      `/practices/${practiceId}/patients/${patientId}/icd`,
      data
    );
    return response;
  },

  /**
   * Raw practice + patient schedule/target payloads (no session merge).
   * Used to categorize historical readings by target mode at measurement time.
   */
  getScheduleTargetSourcesForPatient: async (practiceId, patientId) => {
    const [practiceResponse, patientResponse] = await Promise.all([
      practiceService.getScheduleAndTargets(practiceId, { skipCache: true }).catch(() => null),
      patientId
        ? api.get(`/practices/${practiceId}/patients/${patientId}/schedule-targets`).catch(() => null)
        : Promise.resolve(null)
    ]);
    const practiceData = practiceResponse?.data?.success ? (practiceResponse.data.data || {}) : {};
    const patientPayload = patientResponse?.data?.success ? (patientResponse.data.data || {}) : {};
    const patientScheduleOnly = { ...patientPayload };
    delete patientScheduleOnly.usePatientScheduleTargets;
    return { practiceData, patientData: patientScheduleOnly };
  },

  /**
   * GET practice + patient schedule rows merged using this session's practice vs patient checkbox
   * (see patientScheduleTargetPreference.js).
   */
  getResolvedScheduleTargetsForPatient: async (practiceId, patientId) => {
    const { practiceData, patientData } = await patientService.getScheduleTargetSourcesForPatient(
      practiceId,
      patientId
    );
    const usePatientTargets = getUsePatientScheduleTargetsPreference(practiceId, patientId);
    const effective = resolveDisplayScheduleTargets({
      practiceData,
      patientData,
      usePatientTargets
    });
    if (hasAnyScheduleTargets(effective)) return effective;
    return practiceData;
  },

  reviewPatientAbnormal: async (practiceId, patientId) => {
    const response = await api.put(`/practices/${practiceId}/patients/${patientId}/review`);
    return response;
  },

  unassignPatientAbnormal: async (practiceId, patientId) => {
    const response = await api.put(`/practices/${practiceId}/patients/${patientId}/unassign`);
    return response;
  },

  getPatientAbnormalReadings: async (practiceId, patientId, params = {}) => {
    const response = await api.get(`/practices/${practiceId}/patients/${patientId}/abnormal-readings`, { params });
    return response;
  },

  assignPatientCaregiver: async (practiceId, patientId, data) => {
    const response = await api.put(`/practices/${practiceId}/patients/${patientId}/assign-caregiver`, data);
    return response;
  },

  assignPatientProvider: async (practiceId, patientId, data) => {
    const response = await api.put(`/practices/${practiceId}/patients/${patientId}/assign-provider`, data);
    return response;
  },

  assignMeasurement: async (practiceId, patientId, vitalType, measurementId, data) => {
    const response = await api.put(
      `/practices/${practiceId}/patients/${patientId}/measurements/${vitalType}/${measurementId}/assign`,
      data
    );
    return response;
  },

  unassignMeasurement: async (practiceId, patientId, vitalType, measurementId) => {
    const response = await api.put(
      `/practices/${practiceId}/patients/${patientId}/measurements/${vitalType}/${measurementId}/unassign`
    );
    return response;
  },

  getAssignedAbnormalReviews: async (practiceId = null) => {
    let url = '/patients/assigned-abnormal-reviews';
    if (practiceId && practiceId !== 'all') {
      url = `/practices/${practiceId}/patients/assigned-abnormal-reviews`;
    }
    const response = await api.get(url);
    return response;
  }
};

export default patientService;
